/**
 * /loyalty-signup
 *
 * Public, no-auth enrollment page reached by scanning the QR code shown on
 * the customer-facing display in idle mode. Mobile-first: customers fill in
 * name + email + phone right on their own phone while waiting at the
 * counter, no cashier interaction required.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Crown, Spinner } from "@phosphor-icons/react";

const LoyaltySignupPage = () => {
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  // Pre-fill from URL params — receipt QR carries ?email=…&name=…
  useEffect(() => {
    const e = params.get("email");
    const n = params.get("name");
    if (e) setEmail(e);
    if (n) setName(n);
  }, [params]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    const cleanPhone = phone.replace(/\D/g, "");
    if (!name.trim() || !email.includes("@") || cleanPhone.length < 7) {
      setError("Please enter your name, a valid email, and a phone number.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/loyalty/public/signup`, {
        name: name.trim(), email: email.trim(), phone: cleanPhone,
      });
      setDone(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Sign-up failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="loyalty-signup-success">
        <div className="max-w-sm w-full text-center space-y-4">
          <CheckCircle size={64} weight="fill" className="text-emerald-500 mx-auto" />
          <h1 className="font-heading text-3xl">Welcome aboard, {done.name.split(" ")[0]}!</h1>
          <p className="text-muted-foreground">
            You&apos;re now a <span className="capitalize font-medium text-primary">{done.tier}</span> member.
            Earn 1 point for every $1 you spend. Mention your phone number at the counter to redeem.
          </p>
          <div className="rounded-2xl border border-border bg-card p-4 text-left">
            <p className="text-xs text-muted-foreground mb-1">Your phone (used to look you up)</p>
            <p className="font-mono text-lg">{done.phone}</p>
          </div>
          <p className="text-xs text-muted-foreground pt-2">
            Welcome email sent to <span className="font-medium">{done.email}</span>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="loyalty-signup-root">
      <form onSubmit={handleSubmit} className="max-w-sm w-full space-y-5">
        <div className="text-center space-y-2">
          <Crown size={48} weight="fill" className="text-primary mx-auto" />
          <h1 className="font-heading text-3xl">Join our rewards program</h1>
          <p className="text-sm text-muted-foreground">
            Earn 1 point per $1 spent. Free perks unlock at Silver, Gold and Platinum.
          </p>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ls-name">Your name</Label>
            <Input
              id="ls-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Jamie Smith" autoComplete="name" autoFocus
              data-testid="ls-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ls-email">Email</Label>
            <Input
              id="ls-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" autoComplete="email" inputMode="email"
              data-testid="ls-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ls-phone">Phone</Label>
            <Input
              id="ls-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567" autoComplete="tel" inputMode="tel"
              data-testid="ls-phone"
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2" data-testid="ls-error">{error}</p>
        )}

        <Button
          type="submit" disabled={busy} className="w-full h-12 text-base"
          data-testid="ls-submit"
        >
          {busy ? <Spinner size={18} className="animate-spin mr-2" /> : <Crown size={18} weight="fill" className="mr-2" />}
          {busy ? "Enrolling…" : "Join now"}
        </Button>

        <p className="text-[11px] text-muted-foreground text-center">
          We&apos;ll only use your phone and email to look you up at the counter and send
          occasional rewards. No spam, ever.
        </p>
      </form>
    </div>
  );
};

export default LoyaltySignupPage;
